import type { FeedbackAnswerQuestionMatching } from '@repo/types/questions/QuestionMatching';

import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { QuestionMatchingAnswer } from '@/components/features/quiz/question/question-resolver/question-matching';
import { Form, FormField } from '@/components/ui/form';

import type { QuestionMarkedMatchingFormProps } from '.';

import {
  QuestionMarkedCorrectToggleField,
  QuestionMarkedExplanationField,
  QuestionMarkedFormActions,
  QuestionMarkedPointsField,
  QuestionMarkedResponseCard,
  QuestionMarkedResponseContent,
  QuestionMarkedResponseHeader,
} from '../QuestionMarked';

export function QuestionMarkedMatchingForm(questionMarkedMatchingFormProps: QuestionMarkedMatchingFormProps) {
  const { marked, content, isOpen, onClose, onSubmit: _onSubmit } = questionMarkedMatchingFormProps;

  // Precompute maps for quick lookup
  const leftPointsMap = useMemo<Map<string, number>>(() => new Map(content.answers.map((a) => [a.left.id, a.points])), [content.answers]);
  const leftContentMap = useMemo<Map<string, string>>(() => new Map(content.answers.map((a) => [a.left.id, a.left.content])), [content.answers]);
  const rightContentMap = useMemo<Map<string, string>>(() => new Map(content.answers.map((a) => [a.right.id, a.right.content])), [content.answers]);

  const form = useForm<FeedbackAnswerQuestionMatching>({
    defaultValues: {
      answers: [],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'answers',
    keyName: 'fieldKey',
  });

  const onSubmit = (data: FeedbackAnswerQuestionMatching) => {
    onClose();
    _onSubmit?.(data);
  };

  useEffect(() => {
    if (isOpen && marked) {
      form.reset(marked);
    }
  }, [isOpen, marked, form]);

  return (
    <Form {...form}>
      <form className={'flex w-full flex-col gap-3 p-3'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <div className="space-y-4">
          {fields.map((field, index) => {
            const markedAnswer = field;
            const leftId: string = markedAnswer.match?.left ?? '';
            const rightId: string = markedAnswer.match?.right ?? '';
            const maxPoints: number = leftPointsMap.get(leftId) ?? 0;
            const contentLeft: string = leftContentMap.get(leftId) ?? '';
            const contentRight: string = rightContentMap.get(rightId) ?? '';

            return (
              <QuestionMarkedResponseCard key={field.fieldKey}>
                <QuestionMarkedResponseHeader>
                  <QuestionMarkedCorrectToggleField control={form.control} name={`answers.${index}.correct`} />
                  <QuestionMarkedPointsField control={form.control} name={`answers.${index}.points`} maxPoints={maxPoints} />
                </QuestionMarkedResponseHeader>

                <QuestionMarkedResponseContent>
                  <FormField
                    name={`answers.${index}.correct`}
                    control={form.control}
                    render={({ field: { value } }) => (
                      <div className="grid grow items-stretch gap-2 text-center md:grid-cols-2">
                        <QuestionMatchingAnswer content={contentLeft} correct={value} isSelected={false} isDisabled={false} pairNumber={null} />
                        <QuestionMatchingAnswer content={contentRight} correct={value} isSelected={false} isDisabled={false} pairNumber={null} />
                      </div>
                    )}
                  />
                </QuestionMarkedResponseContent>

                <QuestionMarkedExplanationField control={form.control} name={`answers.${index}.explanation`} />
              </QuestionMarkedResponseCard>
            );
          })}
        </div>
        <QuestionMarkedFormActions onClose={onClose} />
      </form>
    </Form>
  );
}
